import React, { FC, useMemo, useState } from 'react';
import { Box, CircularProgress, Container, Dialog, DialogContent, IconButton, Paper, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ReferenceArea, ReferenceLine, ResponsiveContainer, CartesianGrid } from 'recharts';

import { useGetTrendsQuery } from '../healthWalletSlice';
import type { TrendGroup } from '../healthWalletSlice';
import { computeYDomain, formatDate, formatYTick, formatInterpretation, formatPanelLabel, isAbnormalInterp, panelInterpColor } from '../healthWalletConstants';
import type { SectionProps } from './TabRenderer';
import { bucketTrendGroup, trendBadge, trendWhy } from './classify';
import type { TrendBucket } from './classify';
import { ChartAiSummary, DoctorQuestionsAccordion, useChartSummaryStream } from '../ChartAiSummary';
import { useAiSummaryEnabled } from '../aiSettings';
import { V1_ACCENT, V1_ACCENT_TINT, V1_ACCENT_BORDER } from './theme';
import { V1SearchField } from './V1SearchField';

// ============================================================================
// Trends tab sections (design: Polygon Health Trends Redesign/Trends
// Desktop.dc.html, in the DESIGN palette). Data = get_patient_trends groups
// (series + dominant refLow/refHigh). Buckets/badges/"why" lines are ALL
// deterministic (classify.ts); the only AI = the detail dialog's summary via
// the existing chart-summary path, gated by the Settings AI toggle (easy-win
// policy — Leo 2026-07-13). Doctor questions + "About this measure" =
// "coming soon" placeholders. The detail dialog lives HERE (TrendGroup shape
// ≠ the shared GroupedSummary contract, so ctx.openDetail is unused).
// ============================================================================

const PAGE_WIDTH_SX = { maxWidth: { xs: '100%', sm: '94%' } };
const CARD_LIMIT = 6;

// SINGLE source of truth for the tab's buckets — hero tiles, filter pills, and bucket sections all iterate
// THIS list (present-only), so the three can't drift. `tile` = short hero label; `pill` = filter label;
// `title` = section header; `tileBg/tileFg` = hero tile palette; `dot` = section dot.
const TREND_BUCKETS: Array<{ key: TrendBucket; tile: string; pill: string; title: string; dot: string; tileBg: string; tileFg: string }> = [
  { key: 'attention',  tile: 'Attention',  pill: 'Needs attention',           title: 'Needs attention',           dot: '#e0453a', tileBg: '#fbe6e3', tileFg: '#bd352b' },
  { key: 'changed',    tile: 'Changed',    pill: 'Changed recently',          title: 'Changed recently',          dot: '#e8961e', tileBg: '#fdf0dd', tileFg: '#9a5b00' },
  { key: 'steady',     tile: 'Steady',     pill: 'Steady',                    title: 'Steady & in range',         dot: '#34a760', tileBg: '#e6f6ec', tileFg: '#1a7f3c' },
  { key: 'historical', tile: 'Historical', pill: 'Older out-of-normal-range', title: 'Older out-of-normal-range', dot: '#c0736b', tileBg: '#f6e9e7', tileFg: '#9c4a42' },
  { key: 'dataonly',   tile: 'Not rated',  pill: 'Not rated as high/low',     title: 'Not rated as high/low',     dot: '#c7c7cc', tileBg: '#f0f0f3', tileFg: '#8e8e93' },
];

const refLabel = (t: TrendGroup): string => {
  const u = t.unit ? ` ${t.unit}` : '';
  if (t.refLow != null && t.refHigh != null) return `${t.refLow}–${t.refHigh}${u}`;
  if (t.refHigh != null) return `< ${t.refHigh}${u}`;  // one-sided (incl. parsed "<X" text ranges)
  if (t.refLow != null) return `> ${t.refLow}${u}`;
  return t.refText || 'N/A'; // unparseable text range stays display-only
};

// --- Sparkline (SVG path) — a miniature of the detail chart so the two can't disagree: shared with ObservationsSections.
// Both axes mirror TrendDetailDialog: y = computeYDomain(values + refs) (a flat measure reads flat, not a full-height step);
// x = real time (points spaced by date, not index → same-day readings show a spike, not a spread-out diagonal).
export const Sparkline: FC<{ points: { date: string | null; value: number }[]; color: string; refLow?: number | null; refHigh?: number | null; w?: number; h?: number }> = ({ points, color, refLow, refHigh, w = 120, h = 40 }) => {
  const pad = 3;
  const dated = points.filter((p) => p.date);
  const useTime = dated.length > 1;            // time-scale x only when ≥2 dated points; else fall back to index spacing
  const series = useTime ? dated : points;
  const domainVals = series.map((p) => p.value);
  if (refLow != null) domainVals.push(refLow);
  if (refHigh != null) domainVals.push(refHigh);
  const [yMin, yMax] = computeYDomain(domainVals);
  const yRange = yMax - yMin || 1;
  const toY = (v: number) => pad + (h - pad * 2) * (1 - (v - yMin) / yRange);
  const times = series.map((p) => (useTime ? new Date(p.date as string).getTime() : 0));
  const tMin = Math.min(...times);
  const tRange = (Math.max(...times) - tMin) || 1;
  const n = series.length;
  const toX = (i: number) => useTime
    ? pad + ((times[i] - tMin) / tRange) * (w - pad * 2)
    : pad + i * ((w - pad * 2) / Math.max(1, n - 1));
  const d = n < 2
    ? `M${pad} ${h / 2} L${w - pad} ${h / 2}`
    : series.map((p, i) => `${i ? 'L' : 'M'}${toX(i).toFixed(1)} ${toY(p.value).toFixed(1)}`).join(' ');
  return (
    // display:block removes the inline-SVG baseline gap so the chart's box top sits exactly at the row top
    // (top-aligned with the measurement value, not offset by descender whitespace).
    <svg width={w} height={h} aria-hidden="true" style={{ display: 'block' }}>
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// --- 1. Hero -----------------------------------------------------------------
export const TrendsHero: FC<SectionProps> = () => {
  const { data, isLoading } = useGetTrendsQuery();
  const groups = data ?? [];
  const counts = useMemo(() => {
    const c: Record<TrendBucket, number> = { attention: 0, changed: 0, steady: 0, historical: 0, dataonly: 0 };
    groups.forEach((t) => { c[bucketTrendGroup(t)] += 1; });
    return c;
  }, [groups]);
  if (isLoading) {
    return <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2, display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: V1_ACCENT }} /></Container>;
  }
  const readings = groups.reduce((a, t) => a + t.count, 0);
  const tile = (value: number, label: string, bg: string, fg: string) => (
    <Box sx={{ width: { xs: 92, sm: 104 }, flexShrink: 0, bgcolor: bg, borderRadius: '15px', p: '16px 10px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: fg, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: fg, mt: 0.75, fontWeight: 600 }}>{label}</Typography>
    </Box>
  );
  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
      <Paper elevation={0} sx={{ borderRadius: '20px', p: '24px 26px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 300 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: V1_ACCENT, textTransform: 'uppercase', mb: 1.25 }}>
            Your record at a glance
          </Typography>
          <Typography sx={{ fontSize: 21, fontWeight: 600, lineHeight: 1.4, letterSpacing: '-0.2px', maxWidth: 580 }}>
            We track <strong>{groups.length}</strong> measures across <strong>{readings.toLocaleString('en-US')}</strong> readings
            from your labs and visits.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {TREND_BUCKETS.filter((b) => counts[b.key] > 0).map((b) => (
            <React.Fragment key={b.key}>{tile(counts[b.key], b.tile, b.tileBg, b.tileFg)}</React.Fragment>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

// --- 2. Explainer -------------------------------------------------------------
export const TrendsExplainer: FC<SectionProps> = () => (
  <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2 }}>
    <Box sx={{ bgcolor: V1_ACCENT_TINT, borderRadius: '16px', p: '16px 20px', display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 'none', width: 26, height: 26, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <InfoOutlinedIcon sx={{ color: V1_ACCENT, fontSize: 18 }} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650, mb: 0.4 }}>How to read these trends</Typography>
        <Typography sx={{ fontSize: 13, color: '#4a4a52', lineHeight: 1.5, maxWidth: 820 }}>
          Each card tracks one measurement from your labs and visits over time, compared with the normal range your lab
          reported. Some measures (like many vitals and specialty tests) don&rsquo;t have a single normal range — for those we
          show your readings without a high or low label. This page shows your record as documented; it does not interpret
          results or give medical advice.
        </Typography>
      </Box>
    </Box>
  </Container>
);

// --- Card ---------------------------------------------------------------------
const TrendCard: FC<{ t: TrendGroup; onOpen: () => void }> = ({ t, onOpen }) => {
  const badge = trendBadge(t);
  const panelLabel = formatPanelLabel(t.panel); // latest reading's panel interp — SECONDARY signal beside the range badge
  const vals = t.data.map((p) => p.value);
  const cur = vals[vals.length - 1];
  const prev = vals.length > 1 ? vals[vals.length - 2] : null;
  const delta = prev != null ? Math.round((cur - prev) * 100) / 100 : 0;
  const arrow = prev == null || delta === 0 ? '' : delta > 0 ? '↑' : '↓';
  const changeLabel = prev != null && delta !== 0
    ? `${delta > 0 ? 'Up' : 'Down'} ${Math.abs(delta)}${t.unit ? ` ${t.unit}` : ''} since previous`
    : t.lastDate ? `Last updated ${formatDate(t.lastDate)}` : '';
  return (
    <Box onClick={onOpen} sx={{
      bgcolor: '#fdfcff', border: '1px solid #ece9f4', borderRadius: '14px', p: 2.25, cursor: 'pointer',
      '&:hover': { borderColor: V1_ACCENT_BORDER, bgcolor: 'white' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.25 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 650, letterSpacing: '-0.2px', lineHeight: 1.25, minWidth: 0 }}>{t.name}</Typography>
        <Box sx={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', bgcolor: badge.bg, color: badge.fg, whiteSpace: 'nowrap' }}>
          {badge.label}
        </Box>
      </Box>
      {/* Value + chart share ONE baseline — chart bottom aligned to the measurement (value) line; the change
          caption sits BELOW the row so it doesn't lift the chart off that line (Leo 2026-07-26). Negative
          top margin pulls the value back up (the taller chart row otherwise drops it). */}
      <Box sx={{ mt: -0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', minWidth: 0 }}>
            {cur}{t.unit ? <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: '#8e8e93', ml: 0.5 }}>{t.unit}</Box> : null}
            {arrow ? <Box component="span" sx={{ fontSize: 15, ml: 0.75, color: badge.fg }}>{arrow}</Box> : null}
          </Typography>
          {/* Sparkline color obeys the analyte's OWN flag: red when flagged abnormal, else the range-derived
              color (green only when in-range/steady). Consistent with the Observations card. */}
          <Sparkline points={t.data} refLow={t.refLow} refHigh={t.refHigh}
            color={isAbnormalInterp(t.interpretation) ? '#c0362c' : badge.spark} w={150} h={44} />
        </Box>
        {changeLabel ? <Typography sx={{ fontSize: 11, color: '#8e8e93', mt: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{changeLabel}</Typography> : null}
      </Box>
      <Typography sx={{ fontSize: 12, color: '#6b6b70', mt: 1.25, lineHeight: 1.45 }}>{trendWhy(t)}</Typography>
      {/* Analyte's OWN latest flag (Observation.interpretation) — PRIMARY, same as the obs card. */}
      {formatInterpretation(t.interpretation) && (
        <Typography sx={{ fontSize: 11, fontWeight: 700, mt: 0.75, color: isAbnormalInterp(t.interpretation) ? '#bd352b' : '#8e8e93' }}>
          Interpretation: {formatInterpretation(t.interpretation)}
        </Typography>
      )}
      {panelLabel && (
        <Typography sx={{ fontSize: 11, mt: 0.75, color: '#8e8e93' }}>
          {panelLabel.lead}<strong style={{ color: panelInterpColor(panelLabel.interp) }}>{panelLabel.interp}</strong>.
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5, pt: 1.25, borderTop: '1px solid #f0eef6' }}>
        <Typography sx={{ fontSize: 12, color: '#6b6b70' }}>{t.count.toLocaleString('en-US')} readings</Typography>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: V1_ACCENT, whiteSpace: 'nowrap' }}>View details ›</Typography>
      </Box>
    </Box>
  );
};

// --- Detail dialog --------------------------------------------------------------
type RangeKey = 'all' | '1y' | '3m';

const TrendDetailDialog: FC<{ trend: TrendGroup | null; onClose: () => void }> = ({ trend, onClose }) => {
  const [range, setRange] = useState<RangeKey>('all');
  // AI summary stream (the doctor-questions accordion below fires its own lazy 'questions' stream on expand).
  // Scoped by the analyte KEY (backend chart-context treats the `name` field as the key), falling back to name
  // for any trend without a key.
  const aiOn = useAiSummaryEnabled();
  const ai = useChartSummaryStream(trend?.resource ?? 'observations', trend?.key ?? trend?.name ?? '', aiOn && !!trend);
  if (!trend) return null;
  const badge = trendBadge(trend);
  const dPanel = formatPanelLabel(trend.panel);

  const points = trend.data
    .filter((p) => p.date)
    .map((p) => ({ date: new Date(p.date as string).getTime(), value: p.value }));
  const lastT = points.length ? points[points.length - 1].date : Date.now();
  const cutoff = range === '3m' ? lastT - 91 * 864e5 : range === '1y' ? lastT - 365 * 864e5 : -Infinity;
  const view = points.filter((p) => p.date >= cutoff);
  const domainVals = [...view.map((p) => p.value)];
  if (trend.refLow != null) domainVals.push(trend.refLow);
  if (trend.refHigh != null) domainVals.push(trend.refHigh);
  const [yMin, yMax] = computeYDomain(domainVals);
  const vals = view.map((p) => p.value);
  const stats = vals.length
    ? { min: Math.min(...vals), max: Math.max(...vals), avg: Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 }
    : null;

  const fact = (label: string, value: string, bordered = false) => (
    <Box sx={{ flex: 1, minWidth: 110, ...(bordered ? { borderLeft: '1px solid #f0f0f3', pl: 2 } : {}) }}>
      <Typography sx={{ fontSize: 11, color: '#aeaeb2', fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.5 }}>{value}</Typography>
    </Box>
  );
  const rangePill = (key: RangeKey, label: string) => (
    <Box key={key} onClick={() => setRange(key)} sx={{
      fontSize: 12, fontWeight: 600, px: 1.5, py: 0.5, borderRadius: '16px', cursor: 'pointer',
      bgcolor: range === key ? V1_ACCENT : 'white', color: range === key ? 'white' : '#3a3a3c',
      border: `1px solid ${range === key ? V1_ACCENT : '#e0e0e6'}`,
    }}>{label}</Box>
  );
  const comingSoon = (title: string) => (
    <Paper key={title} elevation={0} sx={{ borderRadius: '16px', border: '1px dashed #e0e0e6', bgcolor: 'white', opacity: 0.55, p: '14px 20px', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#8e8e93', flex: 1 }}>{title}</Typography>
      <Box sx={{ fontSize: '11px', fontWeight: 700, color: '#8e8e93', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>Coming soon</Box>
    </Paper>
  );

  return (
    <Dialog open={!!trend} onClose={onClose} maxWidth="md" fullWidth transitionDuration={0}
      PaperProps={{ sx: { borderRadius: '22px', bgcolor: '#f7f5fb', overflow: 'hidden' } }}>
      <Box sx={{ bgcolor: 'white', p: '24px 26px', borderBottom: '1px solid #ece9f4', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>{trend.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            <Box sx={{ fontSize: '12.5px', fontWeight: 700, px: 1.5, py: 0.6, borderRadius: '20px', bgcolor: badge.bg, color: badge.fg }}>{badge.label}</Box>
            <Typography sx={{ fontSize: '12.5px', color: '#8e8e93' }}>as documented in your health record</Typography>
          </Box>
          {/* Analyte's OWN latest flag (Observation.interpretation) — PRIMARY, above the panel; matches the obs card. */}
          {formatInterpretation(trend.interpretation) && (
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mt: 1, color: isAbnormalInterp(trend.interpretation) ? '#bd352b' : '#8e8e93' }}>
              Interpretation: {formatInterpretation(trend.interpretation)}
            </Typography>
          )}
          {/* Panel interpretation — the latest reading's containing DiagnosticReport conclusion, right below the badge.
              Neutral grey with the interp word bolded (the panel is NOT the color signal). */}
          {dPanel && (
            <Typography sx={{ fontSize: 12, mt: 1, color: '#8e8e93' }}>
              {dPanel.lead}<strong style={{ color: panelInterpColor(dPanel.interp) }}>{dPanel.interp}</strong>.
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ bgcolor: '#ececed', color: '#6b6b70' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <DialogContent sx={{ p: '22px !important' }}>
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: 'white' }}>
          {fact('Latest', `${points.length ? points[points.length - 1].value : '—'}${trend.unit ? ` ${trend.unit}` : ''}`)}
          {fact('Normal range', refLabel(trend), true)}
          {fact('Readings', trend.count.toLocaleString('en-US'), true)}
          {fact('Last updated', trend.lastDate ? formatDate(trend.lastDate) : '—', true)}
        </Paper>

        {/* Chart with the reference band + range pills */}
        <Paper elevation={0} sx={{ borderRadius: '16px', p: '18px 20px', mb: 2, bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: '#6b6b70', textTransform: 'uppercase' }}>
              Trend{trend.unit ? ` (${trend.unit})` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75 }}>{rangePill('3m', '3 mo')}{rangePill('1y', '1 yr')}{rangePill('all', 'All')}</Box>
          </Box>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={view} margin={{ top: 5, right: 12, bottom: 5, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eeeef2" />
              {trend.refLow != null && trend.refHigh != null && (
                <ReferenceArea y1={trend.refLow} y2={trend.refHigh} fill={V1_ACCENT_TINT} fillOpacity={0.55} stroke="none" />
              )}
              {/* One-sided range (incl. parsed "<X"/">X" text ranges) → dashed boundary line. */}
              {trend.refLow != null && trend.refHigh == null && (
                <ReferenceLine y={trend.refLow} stroke={V1_ACCENT} strokeDasharray="4 4" strokeOpacity={0.6} />
              )}
              {trend.refHigh != null && trend.refLow == null && (
                <ReferenceLine y={trend.refHigh} stroke={V1_ACCENT} strokeDasharray="4 4" strokeOpacity={0.6} />
              )}
              <XAxis
                dataKey="date" type="number" scale="time" domain={['dataMin', 'dataMax']}
                tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { year: '2-digit', month: 'short' })}
                tick={{ fontSize: 10 }}
              />
              <YAxis type="number" domain={[yMin, yMax]} tickFormatter={formatYTick} tick={{ fontSize: 10 }} width={45} />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (
                    <Paper sx={{ px: 1.5, py: 0.75, borderRadius: 1.5, boxShadow: 2 }}>
                      <Typography variant="caption" fontWeight={600} display="block">{d.value}{trend.unit ? ` ${trend.unit}` : ''}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {new Date(d.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </Typography>
                    </Paper>
                  );
                }}
              />
              <Line type="monotone" dataKey="value" stroke={V1_ACCENT} strokeWidth={2} dot={{ r: 2.6 }} activeDot={{ r: 4.5 }} />
            </LineChart>
          </ResponsiveContainer>
          {stats ? (
            <Typography sx={{ fontSize: 12, color: '#8e8e93', mt: 1 }}>
              Min {stats.min} · Max {stats.max} · Average {stats.avg}{trend.unit ? ` ${trend.unit}` : ''} over this period
            </Typography>
          ) : null}
        </Paper>

        {/* AI summary — Settings-gated; real-SSE, step-aware; hides on failure. */}
        <ChartAiSummary variant="full" status={ai.status} summary={ai.summary} loading={ai.loading} />

        {/* Questions for your doctor — lazy accordion; fires its own 'questions' stream on expand. `name` carries
            the analyte KEY (backend chart-context contract), fallback to display name. */}
        <DoctorQuestionsAccordion resource={trend.resource} name={trend.key ?? trend.name} enabled={aiOn} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {comingSoon('About this measure')}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// --- 3. Body: pills + bucket sections + cards + internal detail dialog ----------
type FilterKey = 'all' | TrendBucket;

export const TrendsBody: FC<SectionProps> = () => {
  const { data, isLoading } = useGetTrendsQuery();
  const groups = data ?? [];
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [selected, setSelected] = useState<TrendGroup | null>(null);

  const q = search.trim().toLowerCase();
  const matches = (t: TrendGroup) => !q || t.name.toLowerCase().includes(q);

  const buckets = useMemo(() => {
    const b: Record<TrendBucket, TrendGroup[]> = { attention: [], changed: [], steady: [], historical: [], dataonly: [] };
    [...groups].sort((a, c) => c.count - a.count).forEach((t) => { b[bucketTrendGroup(t)].push(t); });
    return b;
  }, [groups]);

  if (isLoading) return null; // hero shows the spinner

  const pill = (key: FilterKey, label: string) => {
    // Hide a classifier pill when its bucket is empty (nothing to show).
    if (key !== 'all' && (buckets[key as TrendBucket]?.length ?? 0) === 0) return null;
    return (
    <Box key={key} onClick={() => setFilter(key)} sx={{
      fontSize: 13, fontWeight: 600, px: 2, py: 1, borderRadius: '20px', cursor: 'pointer',
      bgcolor: filter === key ? V1_ACCENT : 'white', color: filter === key ? 'white' : '#3a3a3c',
      border: `1px solid ${filter === key ? V1_ACCENT : '#e0e0e6'}`,
    }}>{label}</Box>
    );
  };

  // Default-open (Leo 2026-07-14): the actionable pair (attention+changed) when either has items —
  // the tab's original behavior — else the FIRST NON-EMPTY section, so cards are always visible on entry.
  const hasActionable = buckets.attention.length > 0 || buckets.changed.length > 0;
  const firstNonEmpty = TREND_BUCKETS.map((b) => b.key).find((k) => buckets[k].length > 0);
  const defaultOpen = (key: string) =>
    hasActionable ? (key === 'attention' || key === 'changed') : key === firstNonEmpty;

  const section = (key: TrendBucket, title: string, dot: string, list: TrendGroup[]) => {
    const visible = (filter === 'all' || filter === key) && list.filter(matches).length > 0;
    if (!visible) return null;
    const full = list.filter(matches);
    const forced = !!q || filter === key;
    const isOpen = forced ? true : (open[key] ?? defaultOpen(key));
    const all = !!q || !!showAll[key];
    const cards = isOpen ? (all ? full : full.slice(0, CARD_LIMIT)) : [];
    return (
      <Paper key={key} elevation={0} sx={{ borderRadius: '18px', border: `1px solid ${V1_ACCENT_BORDER}`, bgcolor: 'white', overflow: 'hidden' }}>
        <Box onClick={() => setOpen((s) => ({ ...s, [key]: !(s[key] ?? defaultOpen(key)) }))} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '18px 22px', cursor: 'pointer' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dot, flex: 'none' }} />
          <Typography component="h2" sx={{ m: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', flex: 1 }}>{title}</Typography>
          <Box sx={{ fontSize: '12.5px', fontWeight: 700, color: '#6b6b70', bgcolor: '#f0f0f3', px: 1.4, py: 0.5, borderRadius: '20px' }}>{full.length}</Box>
          <Typography sx={{ fontSize: 13, color: '#8e8e93', width: 16, textAlign: 'center' }}>{isOpen ? '▾' : '▸'}</Typography>
        </Box>
        {isOpen && (
          <Box sx={{ px: 2.75, pb: 2.75 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 1.75 }}>
              {cards.map((t, i) => <TrendCard key={`${t.resource}-${t.name}-${i}`} t={t} onOpen={() => setSelected(t)} />)}
            </Box>
            {full.length > CARD_LIMIT && (
              <Typography onClick={() => setShowAll((s) => ({ ...s, [key]: !s[key] }))}
                sx={{ textAlign: 'center', mt: 2, fontSize: 13, fontWeight: 600, color: V1_ACCENT, cursor: 'pointer' }}>
                {all ? 'Show fewer' : `Show all ${full.length} →`}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    );
  };

  const noResults = !!q && !TREND_BUCKETS.some((b) => buckets[b.key].some(matches));

  return (
    <Container maxWidth={false} sx={{ ...PAGE_WIDTH_SX, mt: 2.5, pb: 12 }}>
      <V1SearchField value={search} onChange={setSearch} placeholder="Search measures" />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
        {pill('all', 'All')}
        {TREND_BUCKETS.map((b) => pill(b.key, b.pill))}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {TREND_BUCKETS.map((b) => section(b.key, b.title, b.dot, buckets[b.key]))}
      </Box>
      {noResults && (
        <Typography sx={{ p: '40px 26px', textAlign: 'center', color: '#8e8e93', fontSize: 15 }}>
          No measures match &ldquo;{search}&rdquo;.
        </Typography>
      )}
      <Box sx={{ mt: 4.5, pt: 2.75, borderTop: '1px solid #e2e2e8', textAlign: 'center' }}>
        <Typography sx={{ fontSize: 12, color: '#aeaeb2', lineHeight: 1.55, maxWidth: 660, mx: 'auto' }}>
          Ranges shown are the normal ranges reported by your labs. This page presents your measurements as documented and
          does not interpret results or provide medical advice.
        </Typography>
      </Box>
      <TrendDetailDialog trend={selected} onClose={() => setSelected(null)} />
    </Container>
  );
};
